import React from 'react';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency, formatPct } from '../../shared/utils/dashboard-page.utils';
import { SectionKey, TotalsMode } from '../../shared/types/dashboard-page.types';
import classes from './top-summary-section.module.scss';

export type TopSummarySectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  trendSignClass: string;
  sameDaysComparison: null | { label: 'sameMonth' | 'sameWeek'; baseline: number };
  comparePct: number | null;
  totalsCards: Array<{ key: TotalsMode; title: string; value: number; count?: number }>;
  topKpiRows: { label: string; pct: number }[];
  onTotalsCardClick?: (card: { key: TotalsMode; title: string; value: number; count?: number }) => void;
};

export const TopSummarySection: React.FC<TopSummarySectionProps> = ({
  collapsedSections,
  toggleSection,
  trendSignClass,
  sameDaysComparison,
  comparePct,
  totalsCards,
  topKpiRows,
  onTotalsCardClick,
}) => {
  const isSingleClosedShift = totalsCards.length === 1 && totalsCards[0].key === 'closedShift';
  const isThreeCardOpenShift = totalsCards.length === 3;
  const useEqualSplitLayout = isSingleClosedShift || isThreeCardOpenShift;

  return (
  <SectionCard title="סיכום הזמנות" collapsed={collapsedSections.topSummary} onToggle={() => toggleSection('topSummary')} hideToggle>
    <div className={useEqualSplitLayout ? `${classes.summaryPanel} ${classes.summaryPanelEqual}` : classes.summaryPanel}>
      <div className={classes.summaryRight}>
        {topKpiRows.map((row) => {
          const isDecrease = row.pct <= 0;
          return (
            <div key={row.label} className={classes.kpiRow}>
              <span className={classes.kpiLabel}>{row.label}</span>
              <span className={classes.kpiPct}>{Math.abs(row.pct).toFixed(1)}%</span>
              <span className={isDecrease ? classes.kpiArrowGood : classes.kpiArrowBad}>{isDecrease ? '↓' : '↑'}</span>
            </div>
          );
        })}
      </div>
      <div className={classes.summaryDivider} />
      <div className={classes.summaryLeft}>
        {useEqualSplitLayout ? (
          <p className={classes.summaryHint}>לחץ כדי לראות פירוט הזמנות</p>
        ) : null}
        <div className={totalsCards.length === 1 ? `${classes.totalsGrid} ${classes.totalsGridSingle}` : classes.totalsGrid}>
          {[...totalsCards].reverse().map((card) => {
            const isEmphasized = card.key === 'ordersTotal' || card.key === 'closedShift';
            const isSingleClosedShiftCard = totalsCards.length === 1 && card.key === 'closedShift';
            return (
              <button
                type="button"
                key={card.key}
                className={isSingleClosedShiftCard ? classes.totalCardSingle : (isEmphasized ? classes.totalCardActive : classes.totalCard)}
                onClick={() => onTotalsCardClick?.(card)}
                data-testid={`dashboard-summary-card-${card.key}`}
              >
                <span className={classes.summaryValue}>{asCurrency(card.value)}</span>
                <span className={classes.summarySubLabel}>
                  {card.title}
                  {card.count !== undefined ? ` (${card.count})` : ''}
                </span>
              </button>
            );
          })}
        </div>
        <p className={`${classes.summaryDelta} ${classes[trendSignClass]}`}>
          {sameDaysComparison ? (
            <>
              <span className={classes.summaryDeltaPct} dir="ltr">{formatPct(comparePct)}</span>
              {` לעומת ${sameDaysComparison.label === 'sameMonth' ? 'חודש שעבר' : 'שבוע שעבר'}`}
            </>
          ) : 'אין בסיס להשוואה'}
        </p>
      </div>
    </div>
  </SectionCard>
  );
};
